import React, { useEffect, useState } from 'react';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import baseUrl from '../api/utils';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// ═══════════════════════════════════════════════════════════════════════════════
// SYSADMIN NOTIFICATIONS — same view/edit toggle pattern as MyCompany.jsx:
// field is read-only until "Edit" is clicked, then "Save" persists and locks
// it back to read-only. Holds one setting: how often (in hours) an
// expiring/expired license popup should reappear for a company's ADMIN/CASHIER
// while they're logged in.
// ═══════════════════════════════════════════════════════════════════════════════
const SysAdminNotifications = () => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const token = sessionStorage.getItem("token");

  const [isEditing, setIsEditing] = useState(false);
  const [intervalHours, setIntervalHours] = useState('');
  const [savedInterval, setSavedInterval] = useState(null); // last confirmed value from the server
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // ── GET CURRENT SETTING ────────────────────────────────────
  const fetchInterval = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/api/v2/license/notification-settings`, {
        headers: { Authorization: token },
      });
      if (response.status === 200 && response.data?.intervalHours) {
        setIntervalHours(response.data.intervalHours);
        setSavedInterval(response.data.intervalHours);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate("/unauthorized");
      } else {
        console.error('Error fetching notification settings:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterval();
  }, []);

  const handleEdit = (e) => {
    e.preventDefault();
    setIsEditing(true);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setIntervalHours(savedInterval); // discard any unsaved typing
    setIsEditing(false);
  };

  // ── SAVE SETTING ────────────────────────────────────────────
  const handleSave = async (e) => {
    e.preventDefault();
    const hours = parseInt(intervalHours, 10);
    if (!hours || hours < 1) {
      MySwal.fire({
        icon: 'warning',
        title: 'Invalid value',
        text: 'Please enter a valid number of hours (1 or more).',
      });
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/v2/license/notification-settings?intervalHours=${hours}`,
        {},
        { headers: { Authorization: token } }
      );
      if (response.status === 200) {
        MySwal.fire({
          icon: 'success',
          title: 'Saved!',
          text: `Companies will now be reminded every ${hours} hour(s) about license expiry.`,
          confirmButtonText: 'OK',
        }).then((result) => {
          if (result.isConfirmed) {
            setIsEditing(false);
            fetchInterval(); // re-confirm from the server, don't just trust the local input
          }
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate("/unauthorized");
      } else {
        console.error('Error saving notification settings:', error);
        MySwal.fire('Error', 'Failed to save notification settings!', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="main-body">
        <div className="page-wrapper">
          <div className="card" style={{ margin: "0 -25px 0 -20px", marginTop: "-115px" }}>
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <button className="btn btn-light mr-2" onClick={() => navigate(-1)}>
                  <i className="fa-solid fa-arrow-left"></i>
                </button>
                <h5 className="mb-0 ml-2">Notification Settings</h5>
              </div>

              <p className='text-muted' style={{ fontSize: '14px', maxWidth: '640px' }}>
                Controls how often an expiring or expired license popup reappears for a company's
                ADMIN or CASHIER while they're logged in. Applies to all companies — SaaS and VPS.
              </p>

              {loading ? (
                <div className='text-muted'>Loading current setting…</div>
              ) : (
                <div style={{ maxWidth: '640px' }}>

                  {!isEditing && savedInterval !== null && (
                    <div className='alert alert-secondary py-2 px-3' style={{ display: 'inline-block', fontSize: '13px', marginBottom: '18px' }}>
                      <i className="fa-solid fa-circle-check text-success mr-2"></i>
                      Currently active: <strong>every {savedInterval} hour(s)</strong>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Remind Every (hours) <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          min="1"
                          className={`form-control ${!isEditing ? "bg-light" : ""}`}
                          value={intervalHours}
                          onChange={(e) => setIntervalHours(e.target.value)}
                          readOnly={!isEditing}
                          placeholder="e.g. 2"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="btngrp text-right mt-3">
                    {isEditing ? (
                      <>
                        <button className="btn btn-light mr-2" onClick={handleCancel} disabled={saving}>
                          Cancel
                        </button>
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? "Saving..." : "Save"}
                        </button>
                      </>
                    ) : (
                      <button className="btn btn-success" onClick={handleEdit}>
                        Edit
                      </button>
                    )}
                  </div>

                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SysAdminNotifications;